
import { useState, useEffect, useRef } from "react";
import { Search, MapPin, Loader2 } from "lucide-react";
import { useMapStore } from "@/store/useMapStore";
import { CustomizerConfig } from "@/config/customizerConfig";
import { geocodeAddress, GeocodeResult } from "@/services/mapboxService";
import { useToast } from "@/hooks/use-toast";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface MapTabProps {
  config: CustomizerConfig;
}

const MapTab = ({ config }: MapTabProps) => {
  const { toast } = useToast();
  const { location, setLocation, setLabels } = useMapStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GeocodeResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const skipSearchRef = useRef(false);
  const searchGenRef = useRef(0);

  // Helper function to update labels based on location
  const updateLabelsFromLocation = (lat: number, lng: number, address: string) => {
    const addressParts = address.split(',').map(part => part.trim());
    const city = addressParts[0] || 'UNKNOWN';

    let country = 'UNKNOWN';
    if (addressParts.length > 1) {
      const lastPart = addressParts[addressParts.length - 1];
      if (lastPart === 'USA' || lastPart === 'United States' || /^[A-Z]{2}$/.test(lastPart)) {
        country = 'UNITED STATES';
      } else {
        country = lastPart.toUpperCase();
      }
    }

    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';

    setLabels({
      headline: city.toUpperCase(),
      divider: country.toUpperCase(),
      tagline: `${Math.abs(lat).toFixed(3)}\u00B0${latDir} / ${Math.abs(lng).toFixed(3)}\u00B0${lngDir}`,
      showCoordinates: true,
    });
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length > 2) {
      // Consume skip flag immediately (not inside timeout) to avoid race condition
      if (skipSearchRef.current) {
        skipSearchRef.current = false;
        return;
      }
      searchGenRef.current++;
      const thisGen = searchGenRef.current;
      searchTimeoutRef.current = setTimeout(async () => {
        if (thisGen !== searchGenRef.current) return;
        setIsSearching(true);
        try {
          const currentLocation = useMapStore.getState().location;
          const proximity = currentLocation ? { lng: currentLocation.lng, lat: currentLocation.lat } : undefined;
          const results = await geocodeAddress(searchQuery, proximity);
          if (thisGen !== searchGenRef.current) return;
          setSearchResults(results);
          setOpen(true);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Failed to search for location. Please try again.";
          toast({
            title: "Search Error",
            description: errorMessage,
            variant: "destructive",
          });
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    } else {
      searchGenRef.current++; // invalidate any in-flight request when query shortened
      setSearchResults([]);
      setOpen(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, toast]);

  const handleSelectLocation = (result: GeocodeResult) => {
    const [lng, lat] = result.center;

    setLocation({
      lat,
      lng,
      zoom: 12,
      address: result.place_name,
    });

    updateLabelsFromLocation(lat, lng, result.place_name);
    searchGenRef.current++;
    skipSearchRef.current = true;
    setSearchQuery(result.place_name);
    setOpen(false);

    toast({
      title: "Location Selected",
      description: `Selected: ${result.text}`,
    });
  };

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newZoom = parseInt(e.target.value, 10);
    if (location) {
      setLocation({ ...location, zoom: newZoom });
    }
  };

  const formatLat = () => {
    if (!location) return "--";
    const dir = location.lat >= 0 ? "N" : "S";
    return `${Math.abs(location.lat).toFixed(4)}\u00B0 ${dir}`;
  };

  const formatLng = () => {
    if (!location) return "--";
    const dir = location.lng >= 0 ? "E" : "W";
    return `${Math.abs(location.lng).toFixed(4)}\u00B0 ${dir}`;
  };

  return (
    <div className="p-6 space-y-6">
      <h3
        className="text-lg mb-4"
        style={{ fontFamily: "var(--font-heading)", color: "var(--brand-text)" }}
      >
        Choose Your Location
      </h3>

      {/* Search input */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative mb-6">
            {isSearching ? (
              <Loader2
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin"
                style={{ color: "var(--brand-light)" }}
              />
            ) : (
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5"
                style={{ color: "var(--brand-light)" }}
              />
            )}
            <input
              type="text"
              placeholder="Search any address or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border bg-white text-sm transition-all outline-none"
              style={{
                fontFamily: "var(--font-body)",
                borderColor: "var(--brand-border)",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "var(--sage-400)";
                e.currentTarget.style.boxShadow = "0 0 0 2px var(--sage-100)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "var(--brand-border)";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
          <Command>
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {searchResults.map((result) => (
                  <CommandItem
                    key={result.id}
                    onSelect={() => handleSelectLocation(result)}
                    className="cursor-pointer"
                  >
                    <MapPin className="mr-2 h-4 w-4" style={{ color: "var(--sage-400)" }} />
                    <span>{result.place_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Lat / Lng / Zoom key-value rows */}
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: "var(--brand-muted)", fontFamily: "var(--font-body)" }}>
            Latitude
          </span>
          <span
            className="font-medium"
            style={{ color: "var(--brand-text)", fontFamily: "var(--font-body)" }}
          >
            {formatLat()}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: "var(--brand-muted)", fontFamily: "var(--font-body)" }}>
            Longitude
          </span>
          <span
            className="font-medium"
            style={{ color: "var(--brand-text)", fontFamily: "var(--font-body)" }}
          >
            {formatLng()}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: "var(--brand-muted)", fontFamily: "var(--font-body)" }}>
            Zoom Level
          </span>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0"
              max="20"
              value={location?.zoom ?? 12}
              onChange={handleZoomChange}
              className="w-24"
              style={{ accentColor: "var(--sage-400)" }}
            />
            <span
              className="font-medium"
              style={{ color: "var(--brand-text)", fontFamily: "var(--font-body)" }}
            >
              {location?.zoom ?? 12}
            </span>
          </div>
        </div>
      </div>

      {/* Pro Tip box */}
      <div
        className="mt-6 p-4 rounded-xl"
        style={{ backgroundColor: "var(--sage-50)" }}
      >
        <p
          className="text-sm font-medium mb-1"
          style={{ color: "var(--sage-700)", fontFamily: "var(--font-body)" }}
        >
          Pro Tip
        </p>
        <p
          className="text-xs"
          style={{ color: "var(--sage-600)", opacity: 0.7, fontFamily: "var(--font-body)" }}
        >
          Zoom in for detailed street-level art. Zoom out for a broader city overview with major landmarks.
        </p>
      </div>
    </div>
  );
};

export default MapTab;
